import { FacetRenderer } from "../rendering/renderable/Renderable";
import { DoubleSide, FrontSide, LineSegments, Mesh, Plane } from "three";
import { renderer } from "../rendering/RendererGlobals";
import { Vector3 } from "flavr-math";
import { ImageRenderable } from "../rendering/image/ImageRenderable";
import { MeshRenderable } from "../rendering/mesh/MeshRenderable";
import { now } from "../../messages/timing";
const tmpVector3_1 = new Vector3();
export class ClippingMeshRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.clipPlanes = [];
        this.clipData = [];
        this.maxSize = 1;
        this.update = () => {
            if (this.facet === undefined)
                return;
            const time = now();
            const animationTime = 1 - (Math.floor(time * 0.0005) + 1 - time * 0.0005);
            for (let i = 0, len = this.clipPlanes.length; i < len; ++i) {
                const data = this.clipData[i];
                const plane = this.clipPlanes[i];
                const p = this.facet.planes[i];
                plane.constant = data.startConstant + animationTime * data.length * p[3] * this.maxSize;
            }
        };
        this.clipData.push({
            startConstant: 0,
            length: 1
        });
        this.clipPlanes.push(new Plane(new Vector3(0, -1, 0), 1));
    }
    destroy() {
        var _a;
        const mesh = this.owner.getRenderableByType(MeshRenderable);
        if (mesh)
            (_a = mesh.meshInstance.group) === null || _a === void 0 ? void 0 : _a.traverse((child) => {
                if (child instanceof Mesh || child instanceof LineSegments) {
                    if (Array.isArray(child.material)) {
                        for (const mat of child.material) {
                            mat.clippingPlanes = [];
                            mat.side = FrontSide;
                            mat.needsUpdate = true;
                        }
                    }
                    else {
                        child.material.clippingPlanes = [];
                        child.material.side = FrontSide;
                        child.material.needsUpdate = true;
                    }
                }
            });
    }
    setVisible(visible) {
        var _a;
        const mesh = this.owner.getRenderableByType(MeshRenderable);
        if (mesh)
            (_a = mesh.meshInstance.group) === null || _a === void 0 ? void 0 : _a.traverse((child) => {
                if (child instanceof Mesh || child instanceof LineSegments) {
                    if (Array.isArray(child.material)) {
                        for (const mat of child.material) {
                            mat.clippingPlanes = visible ? this.clipPlanes : null;
                            mat.needsUpdate = true;
                        }
                    }
                    else {
                        child.material.clippingPlanes = visible ? this.clipPlanes : null;
                        child.material.needsUpdate = true;
                    }
                }
            });
    }
    updateFacet(facet) {
        const numPlanes = facet.planes.length;
        for (let i = 0; i < numPlanes; ++i) {
            if (i >= this.clipData.length) {
                this.clipData.push({
                    startConstant: 0,
                    length: 1
                });
            }
            if (i >= this.clipPlanes.length) {
                this.clipPlanes.push(new Plane(new Vector3(0, -1, 0), 0));
            }
        }
        while (this.clipData.length > numPlanes) {
            this.clipData.pop();
            this.clipPlanes.pop();
        }
        const image = this.owner.getFacetRenderer(ImageRenderable);
        if (image) {
            image.material.clipping = true;
            image.material.clippingPlanes = this.clipPlanes;
            image.material.needsUpdate = true;
        }
        else {
            const mesh = this.owner.getRenderableByType(MeshRenderable);
            if (mesh) {
                mesh.onLoadedDelegate.add(() => {
                    this.maxSize = this.traverseObject(mesh);
                });
                this.maxSize = this.traverseObject(mesh);
            }
            else {
                return;
            }
        }
        renderer.localClippingEnabled = true;
        if (facet.animator != undefined) {
            this.registerAnimationCallback(this.update);
        }
        else {
            this.unregisterAnimationCallback();
            this.updateTransform();
        }
    }
    traverseObject(mesh) {
        var _a;
        let maxSize = 1;
        (_a = mesh.meshInstance.group) === null || _a === void 0 ? void 0 : _a.traverse((child) => {
            var _a, _b;
            if (child instanceof Mesh || child instanceof LineSegments) {
                child.geometry.computeBoundingSphere();
                const planes = this.clipPlanes;
                for (const _plane of planes) {
                    maxSize = maxSize < ((_a = child.geometry.boundingSphere) === null || _a === void 0 ? void 0 : _a.radius) / 2 ? ((_b = child.geometry.boundingSphere) === null || _b === void 0 ? void 0 : _b.radius) / 2 : maxSize;
                }
                if (Array.isArray(child.material)) {
                    for (const mat of child.material) {
                        mat.clippingPlanes = this.clipPlanes;
                        mat.side = DoubleSide;
                        mat.needsUpdate = true;
                    }
                }
                else {
                    child.material.clippingPlanes = this.clipPlanes;
                    child.material.side = DoubleSide;
                    child.material.needsUpdate = true;
                }
            }
        });
        return maxSize;
    }
    updateTransform() {
        if (this.facet === undefined)
            return;
        for (let i = 0, len = this.clipPlanes.length; i < len; ++i) {
            const data = this.clipData[i];
            const plane = this.clipPlanes[i];
            const p = this.facet.planes[i];
            const normal = tmpVector3_1.fromArray(p);
            plane.normal.copy(normal);
            data.length = Math.abs(this.owner.worldScale.dot(plane.normal));
            data.startConstant = -this.owner.worldPosition.dot(plane.normal) - data.length * 0.5;
            normal.applyQuaternion(this.owner.worldOrientation);
            plane.constant = data.startConstant + data.length * p[3] * this.maxSize;
        }
    }
}
